import { useEffect, useRef } from 'react';
import { clsx } from 'clsx';

import PlanCard from './PlanCard';

import styles from './PlansPanel.module.scss';

import AnalyticsEventHandler from '@/services/analytics/AnalyticsEventHandler';
import { type Plan } from '@/utils/mapProductObject';
import Loader from '@/components/common/Loader';

interface PlansPanelProps {
  plans: Plan[];
  isLoading: boolean;
  onPlanClick: (plan: Plan) => () => void;
}

// must be aligned with styles
const MOBILE_WIDTH_BREKPOINT_PIXELS = 850;
const MOBILE_COLUMN_WIDTH_PIXELS = 280;
const COLUMN_GAP_PIXELS = 24;

const PlansPanel = ({ plans, isLoading, onPlanClick }: PlansPanelProps) => {
  const plansContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isLoading && plans.length > 0) {
      AnalyticsEventHandler.trackLoaded();

      const bestValuePlanIndex = plans.findIndex((plan) => plan.showBestValueLabel);

      // if best value plan is not the first one, scroll to it on mobile
      if (window.innerWidth < MOBILE_WIDTH_BREKPOINT_PIXELS && plansContainerRef.current && bestValuePlanIndex > 0) {
        const totalGridWidth = MOBILE_COLUMN_WIDTH_PIXELS * plans.length + COLUMN_GAP_PIXELS * (plans.length - 1);
        const scrollableWidth = totalGridWidth - plansContainerRef.current.clientWidth;

        plansContainerRef.current.scrollLeft = (scrollableWidth / plans.length) * (bestValuePlanIndex + 1);
      }
    }
  }, [plans, isLoading]);

  return isLoading ? (
    <div className={clsx(styles.container, styles.plansCount1)}>
      <div className={styles.loaderWrapper}>
        <Loader />
      </div>
    </div>
  ) : (
    <div className={clsx(styles.container, !isLoading && styles[`plansCount${plans.length}`])} ref={plansContainerRef}>
      {plans.map((plan) => (
        <PlanCard plan={plan} key={plan.sku} onButtonClick={onPlanClick(plan)} />
      ))}
    </div>
  );
};

export default PlansPanel;
